import React, {useRef, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {Popover, Transition} from '@headlessui/react'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

import {LexicalComposer} from "@lexical/react/LexicalComposer";
import {RichTextPlugin} from "@lexical/react/LexicalRichTextPlugin";
import {ContentEditable} from "@lexical/react/LexicalContentEditable";
import {HistoryPlugin} from "@lexical/react/LexicalHistoryPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import {HeadingNode, QuoteNode} from "@lexical/rich-text";
import {TableCellNode, TableNode, TableRowNode} from "@lexical/table";
import {ListItemNode, ListNode} from "@lexical/list";
import {CodeHighlightNode, CodeNode} from "@lexical/code";
import {AutoLinkNode, LinkNode} from "@lexical/link";
import {LinkPlugin} from "@lexical/react/LexicalLinkPlugin";
import {ListPlugin} from "@lexical/react/LexicalListPlugin";
import {TablePlugin} from "@lexical/react/LexicalTablePlugin";
import {$generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import classNames from "classnames";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import AutoLinkPlugin from "./plugins/AutoLinkPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import editorTheme from "./themes/editorTheme";
import {
    $createParagraphNode,
    $getRoot,
    $getSelection,
    $insertNodes,
    $isTextNode,
} from "lexical";
import {HorizontalRuleNode} from "@lexical/react/LexicalHorizontalRuleNode";
import ImagesPlugin from "./plugins/ImagePlugin";
import {ImageNode} from "./nodes/ImageNode";
import DropdownMenu from "../common/DropdownMenu";
import {get, isEmpty} from "lodash";
import Button from "../common/Button";
import DragDropPaste from "./plugins/DragDropPastePlugin";
import AwesomeIcon from "../AwesomeIcon";
import {faPlus} from "@fortawesome/pro-regular-svg-icons";
import {faPaperPlaneTop, faMicrophone, faWaveformLines, faCheckCircle, faArrowRotateLeft, faSparkles, faChevronDown} from "@fortawesome/pro-solid-svg-icons";
import {faWandMagicSparkles} from '@fortawesome/pro-regular-svg-icons'
import TextInput from "../FormComponents/TextInput";
import getAssist from "./services/getAssist";
import PatientBio from "./PatientBio";

const Placeholder = () => {
    return (
        <div className="text-gray-500 overflow-hidden absolute overflow-ellipsis top-5 left-3 pointer-events-none inline-block select-none"/>
    );
};

const ReportEditor = (props) => {
    const {impression, gptClinicalSummary, onChange, isMobile, isFocused, templatesList, saveTemplateMode} = props;
    const editorRef = useRef();
    const [search, setSearch] = useState("");
    const [messagesContext, setMessagesContext] = useState([])
    const [aiAssistText, setAIAssistText] = useState('')
    const [aiLoading, setAILoading] = useState(false)
    const [aiGeneratedResponse, setAIGeneratedResponse] = useState(false)
    const [focused, setFocused] = React.useState(false)
    const [isAninating, setAnimating] = useState(true)

  useEffect(() => {
    setTimeout(()=>{
      setAnimating(false)
    }, 4000)
  }, []);
    const onFocus = () => setFocused(true)
    const onBlur = () => setFocused(false)

    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition()


    const onPressMic= ()=>{
        if (!listening) {
            setAIAssistText('')
            SpeechRecognition.startListening()
        } else {
            SpeechRecognition.stopListening()
        }
    }

    useEffect(() => {
        if(!listening){
            setAIAssistText(transcript)
            resetTranscript()
        }
    }, [listening]);

    function iterateWithDelay(inputString) {
        let index = 0;

        const intervalId = setInterval(() => {
            if (index < inputString.length) {
                console.log({len: inputString.length, str: inputString[index]})
                setAIAssistText(aiAssistText+inputString[index])

                index++;
            } else {
                clearInterval(intervalId); // Stop the interval when iteration is done
            }
        }, 50);
    }


    const editorConfig = {
        namespace: "PlaygroundEditor",
        theme: editorTheme,
        // Handling of errors during update
        onError(error) {
            throw error;
        },
        // Any custom nodes go here
        nodes: [
            HeadingNode,
            ListNode,
            ListItemNode,
            QuoteNode,
            CodeNode,
            CodeHighlightNode,
            TableNode,
            TableCellNode,
            TableRowNode,
            AutoLinkNode,
            LinkNode,
            HorizontalRuleNode,
            ImageNode,
        ],
    };

    const onApplyTemplate = (template) => {
        editorRef.current.update(() => {
            const selection = $getSelection();
            if (template && selection && editorRef) {
                const parser = new DOMParser();
                const dom = parser.parseFromString(template, "text/html");
                const nodes = $generateNodesFromDOM(editorRef.current, dom);
                selection.insertNodes(nodes);
            }
        });
        setSearch("");
    };

    const getTemplateSelectMenu = () => {
        let filteredTemplateList = templatesList;
        if (search) {
            filteredTemplateList = templatesList.filter((item) =>
                get(item, ["name"], "").toLowerCase().includes(search?.toLowerCase())
            );
        }
        return filteredTemplateList?.map((item) => {
            return {
                name: (
                    <div className="flex px-4 py-3 items-center justify-start">
                        <p className="text-md">{item.name}</p>
                    </div>
                ),
                onClick: () => onApplyTemplate(item.template),
            };
        });
    };

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.update(() => {
                if (impression || aiGeneratedResponse) {
                    const parser = new DOMParser();
                    let impressionData = aiGeneratedResponse || impression
                    const hasHTML = impressionData.match(/<(?:"[^"]*"['"]|'[^']'['"]*|[^'">])+>/g)
                    impressionData = !hasHTML? impressionData.replace('\n', '<br/>'): impressionData
                    const dom = parser.parseFromString(impressionData, "text/html");
                    let nodes = $generateNodesFromDOM(editorRef.current, dom);
                    nodes = nodes.map((node) => {
                        if ($isTextNode(node)) {
                            return $createParagraphNode().append(node);
                        }
                        return node;
                    });
                    $getRoot().clear().select();
                    $insertNodes(nodes);
                }
            });
        }
    }, [editorRef, impression, aiGeneratedResponse]);

    const FocusUnfocusPlugin = () => {
        const [editor] = useLexicalComposerContext();
        useEffect(() => {
            if (editor && isMobile) {
                if (isFocused) {
                    editor.focus();
                } else {
                    editor.blur();
                }
            }
        }, [editor, isFocused]);
        return null;
    };

    const CustomOnChangePlugin = () => {
        const [editor] = useLexicalComposerContext();

        editorRef.current = editor;
        useEffect(() => {
            return editor.registerUpdateListener(({editorState}) => {
                editorState.read(() => {
                    const md = $generateHtmlFromNodes(editor);
                    onChange(md);
                });
            });
        }, [editor]);
        return null;
    };

    const onStopEventBubbling = (e) => {
        e.stopPropagation();
    };

    const onClickMarkNormal = ()=>{
        onInputEnter('normal')
    }

    const onInputEnter = async (e) => {
        setAILoading(true)
        try {
            const resp = await getAssist({
                prompt: typeof e === 'string' ? 'Mark report as normal' : aiAssistText,
                report: aiGeneratedResponse || impression,
                messagesContext
            })

            setAILoading(false)
            setAIAssistText('')
            const assistantContext = get(resp, ['answer'], {})
            const userContext = get(resp, ['context'], {})
            const newMessagesContext = [userContext, assistantContext]
            setMessagesContext([...messagesContext, ...newMessagesContext])
            setAIGeneratedResponse(assistantContext?.content)
        } catch (e) {
            setAILoading(false)
        }
    }

    const onClickRevertReport = () => {
        setAIGeneratedResponse(impression)
    }

    return (
        <>
            <div className={classNames("relative mt-3 flex justify-between items-center mx-6", {
                'hidden': saveTemplateMode
            })}>
                <div className='flex items-center mb-3'>
                    {!saveTemplateMode && <PatientBio isSimple/>}
                    <Popover className="ml-4">
                    {({open}) => (
                        <>
                            <Popover.Button className={"focus:outline-none"}>
                                <div className={classNames('uppercase p-3 text-teal-400 hover:text-teal-300 text-[14px]', {
                                    'text-teal-300': open,
                                  'animate-text !text-brand-peach': isAninating
                                })}>
                                    <AwesomeIcon
                                        name={faSparkles}
                                        style={'--fa-animation-iteration-count: 2;'}
                                        className={classNames("mr-3 ", {
                                            'fa-beat-fade': open
                                        })}
                                    />
                                    <span className={classNames('mr-3 ', {
                                    })}>Clinical summary</span>

                                    <AwesomeIcon name={faChevronDown}
                                                 className={classNames('transform transition-transform duration-500', {
                                                     'rotate-180' : open
                                                 })}/>
                                </div>
                            </Popover.Button>
                                <Popover.Panel
                                    className="absolute left-0 w-[70%] z-[99999] translate-x-0 text-gray-300 bg-teal-800 shadow-xl mt-2 rounded-md">
                                    <div>
                                        <div className="p-5 max-h-[250px] overflow-y-auto">
                                            {gptClinicalSummary}
                                        </div>
                                        <div className="text-teal-400 border-t border-t-teal-500 p-5 text-[12px]">
                                            Clinical summary auto-generated using available patient data
                                        </div>
                                    </div>
                                </Popover.Panel>
                        </>
                    )}
                </Popover>
                </div>

                {!isEmpty(templatesList) &&(<DropdownMenu
                    isSearch={true}
                    search={search}
                    setSearch={setSearch}
                    options={getTemplateSelectMenu()}
                    Icon={() => (
                        <Button
                            isSecondary
                            className="grow-1 flex space-x-2 px-4 py-2"
                            isPrimary={false}
                        >
                            <AwesomeIcon name={faPlus} size="sm" className="w-5"/>
                            <p className="text-md">Add Template</p>
                        </Button>
                    )}
                />)}
            </div>
          <div className={classNames("w-full h-full", {
            'rounded-md animate-border bg-gradient-to-r from-red-500 via-brand-peach to-transparent bg-[length:400%_400%] p-2': isAninating
          })}>
          <div
                className="flex flex-col h-full w-full p-1 overflow-hidden"
                onScroll={onStopEventBubbling}
                onTouchMove={onStopEventBubbling}
                onTouchStart={onStopEventBubbling}
                onTouchEnd={onStopEventBubbling}
            >
                <LexicalComposer initialConfig={{
                    ...editorConfig,
                    editable: !aiLoading
                }}>
                    <div
                        className={classNames(
                            "flex flex-col bg-gray-50 text-black relative w-full h-full overflow-x-auto text-left rounded-tl rounded-tr overflow-y-hidden editor-shell"
                        )}
                    >
                        <ToolbarPlugin/>
                        <RichTextPlugin
                            contentEditable={
                                <ContentEditable
                                    className="editor-input flex flex-col h-full w-full overflow-y-auto p-3"/>
                            }
                            placeholder={<Placeholder template={impression}/>}
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                        <HistoryPlugin/>
                        <TablePlugin hasCellBackgroundColor/>
                        <CodeHighlightPlugin/>
                        <ListPlugin/>
                        <LinkPlugin/>
                        <AutoLinkPlugin/>
                        <FocusUnfocusPlugin/>
                        <CustomOnChangePlugin/>
                        <ListMaxIndentLevelPlugin maxDepth={7}/>
                        <ImagesPlugin/>
                        <DragDropPaste/>
                    </div>
                </LexicalComposer>
                <div className={classNames('w-full bg-[#cce6e6] p-5', {
                    'opacity-70': aiLoading,
                    'hidden': saveTemplateMode
                })}>
                    <div className="flex justify-between text-gray-700 uppercase text-[14px] mb-3 font-bold tracking-wide">
                        <div>
                            <AwesomeIcon
                                name={faWandMagicSparkles}
                                className={classNames("text-teal-500 mr-2", {
                                    "fa-beat-fade": aiLoading
                                })}
                            />
                            Swift editing
                        </div>
                        <div onClick={onClickRevertReport}
                             className={classNames('text-red-400 cursor-pointer hover:text-red-300 transition-colors duration-300', {
                                 'pointer-events-none': aiLoading
                             })}>
                            <AwesomeIcon
                                name={faArrowRotateLeft}
                                className='mr-2'
                            />
                            <span>Reset</span>
                        </div>
                    </div>
                    <div className='flex w-full opacity-90'>
                        <TextInput
                            placeholder="Enter keywords or commands to make changes"
                            name="aiCompletion"
                            wrapperClassName="bg-gray-50 !p-1 w-full !rounded-xl shadow-xl dark:border-teal-500 !text-[14px] text-black"
                            required
                            readOnly={aiLoading}
                            inputClassName='text-gray-900 placeholder:italic'
                            parentClassName='p-0'
                             className=''
                            value={aiAssistText}
                            onFocus={onFocus} onBlur={onBlur}
                            onEnter={onInputEnter}
                            onChange={(val) => setAIAssistText(val)}
                            rightContent={() => {
                                return (<div className="flex justify-end text-[14px]">
                                    <div  className={classNames('flex justify-end ', {
                                        'text-red-900': focused
                                    })}>
                                        <div onClick={onInputEnter}
                                            className={classNames("opacity-0 visibility-hidden cursor-pointer transition-transform p-2 flex items-center text-gray-300 hover:text-gray-600 transition-colors duration-300", {
                                            'translate-x-[140px] text-gray-600 opacity-100': !isEmpty(aiAssistText) || focused || listening,

                                        })}>
                                            <AwesomeIcon
                                                size='xl'
                                                name={faPaperPlaneTop}
                                                className="block mr-2 cursor-pointer "
                                            />
                                        </div>
                                        <div onClick={onClickMarkNormal}
                                            className={classNames("cursor-pointer transition-[transform, colors, opacity] p-2 pl-4 flex items-center  text-teal-600 hover:text-teal-400 duration-200", {
                                                'translate-x-[200px] opacity-0 visibility-hidden': focused || !isEmpty(aiAssistText) || listening
                                            })}>
                                            <AwesomeIcon
                                                name={faCheckCircle}
                                                className="block mr-2 cursor-pointer"
                                            />
                                            Mark as normal
                                        </div>
                                    </div>

                                    <div onClick={onPressMic}
                                        className={classNames('bg-teal-500 rounded-r-xl ml-4 hover:bg-teal-600 transition-colors duration-500', {
                                            '!bg-brand-peach hover:!bg-brand-peach': listening
                                        })}>
                                        {listening? <AwesomeIcon
                                            name={faWaveformLines}
                                            className="fa-beat-fade block mr-2 p-5 cursor-pointer text-slate-100"
                                        />: <AwesomeIcon
                                            name={faMicrophone}
                                            className={classNames("block mr-2 p-5 cursor-pointer text-slate-100 	", {
                                                'opacity-50 pointer-events-none': !browserSupportsSpeechRecognition
                                            })}
                                        />}


                                    </div>
                                </div>)
                            }}/>
                    </div>
                </div>
            </div>
            </div>

        </>
    );
};

ReportEditor.defaultProps = {
    impression: "",
    onChange: () => {
    },
};

ReportEditor.propTypes = {
    impression: PropTypes.string,
    onChange: PropTypes.func,
    templatesList: PropTypes.arrayOf(PropTypes.string),
    isFocused: PropTypes.bool.isRequired,
};
export default ReportEditor;
